import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiPatch, apiDelete, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { CopyIcon } from "@/pages/ClientPortal";
import { VoiceChip, voiceColor } from "@/lib/voiceColors";
import { periodRange, shiftAnchor, labelRange, postDateOf, isSameDay } from "@/lib/period";
import {
  Plus,
  Trash2,
  Pencil,
  Calendar as CalendarIcon,
  ExternalLink,
  Upload,
  Image as ImageIcon,
  FileText,
  Film,
  Link2,
  X,
  AlertTriangle,
  Eye,
  MessageSquare,
  CheckCircle2,
  Sparkles,
  Copy,
  Kanban,
  LayoutGrid,
  Table2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Download,
  Loader2,
} from "lucide-react";

const STATUSES = [
  { k: "draft", label: "draft", color: "zinc" },
  { k: "review", label: "review", color: "amber" },
  { k: "published", label: "published", color: "emerald" },
];

// Prominent status-strip meta (mirrors the client portal, dark-theme colors)
const STATUS_STRIP = [
  { k: "all",       label: "All",       fg: "text-zinc-50",      bg: "bg-zinc-800/60",   ring: "ring-zinc-700"   },
  { k: "draft",     label: "Draft",     fg: "text-zinc-100",     bg: "bg-zinc-800/40",   ring: "ring-zinc-700"   },
  { k: "review",    label: "In review", fg: "text-amber-300",    bg: "bg-amber-500/10",  ring: "ring-amber-700"  },
  { k: "published", label: "Published", fg: "text-emerald-300",  bg: "bg-emerald-500/10", ring: "ring-emerald-700" },
];

const VIEW_META = {
  kanban:   { label: "Board",    Icon: Kanban },
  gallery:  { label: "Gallery",  Icon: LayoutGrid },
  list:     { label: "List",     Icon: Table2 },
  timeline: { label: "Timeline", Icon: CalendarDays },
};

const VIEW_KEY = "ll_posts_view";
const PERIOD_KEY = "ll_posts_period";

export default function Posts() {
  const { workspaceId } = useParams();
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({ draft: 0, review: 0, published: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "kanban");
  const [period, setPeriod] = useState(() => localStorage.getItem(PERIOD_KEY) || "week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [voiceFilter, setVoiceFilter] = useState(() => localStorage.getItem("ll_posts_voice") || "");

  useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);
  useEffect(() => { localStorage.setItem(PERIOD_KEY, period); }, [period]);
  useEffect(() => { localStorage.setItem("ll_posts_voice", voiceFilter || ""); }, [voiceFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const [list, c, vps] = await Promise.all([
        apiGet(`/workspaces/${workspaceId}/posts`),
        apiGet(`/workspaces/${workspaceId}/posts/counts`),
        apiGet(`/workspaces/${workspaceId}/voice_profiles`).catch(() => []),
      ]);
      setPosts(list || []);
      setCounts(c || {});
      setVoiceProfiles(vps || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (!window.confirm(
      "Import your already-published LinkedIn posts into the board?\n\nThis re-scrapes LinkedIn for any new posts, then adds every published post that isn't on the board yet. Existing cards are never duplicated. Can take 1-3 minutes."
    )) return;
    setBackfilling(true);
    const tid = toast.loading("Importing published posts… scraping LinkedIn (1-3 min).");
    try {
      const start = await apiPost(`/workspaces/${workspaceId}/posts/backfill-published`, { rescrape: true });
      const runId = start?.run_id;
      const poll = async () => {
        try {
          const s = await apiGet(`/workspaces/${workspaceId}/posts/backfill-published/status`, { run_id: runId });
          if (s?.status === "succeeded") {
            const r = s.result || {};
            const n = r.created || 0;
            toast.success(`Imported ${n} published post${n === 1 ? "" : "s"} to the board.`, { id: tid });
            setBackfilling(false);
            load();
            return;
          }
          if (s?.status === "failed") {
            toast.error(s.error || "Backfill failed", { id: tid });
            setBackfilling(false);
            return;
          }
          setTimeout(poll, 4000);
        } catch {
          setTimeout(poll, 5000);
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start import", { id: tid });
      setBackfilling(false);
    }
  };

  const removePost = async (id) => {
    if (!window.confirm("Delete this post and its uploaded files?")) return;
    try {
      await apiDelete(`/workspaces/${workspaceId}/posts/${id}`);
      toast.success("Post deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const moveStatus = async (post, newStatus) => {
    try {
      await apiPatch(`/workspaces/${workspaceId}/posts/${post.id}`, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
      load();
    } catch {
      toast.error("Move failed");
    }
  };

  const [rangeStart, rangeEnd] = useMemo(() => periodRange(period, anchor), [period, anchor]);

  const rangedPosts = useMemo(() => {
    return posts.filter((p) => {
      if (voiceFilter && p.voice_profile_id !== voiceFilter) return false;
      const d = postDateOf(p);
      if (!d) return true; // dateless posts remain visible
      return d >= rangeStart && d <= rangeEnd;
    });
  }, [posts, rangeStart, rangeEnd, voiceFilter]);

  const grouped = useMemo(() => ({
    draft:     rangedPosts.filter((p) => p.status === "draft"),
    review:    rangedPosts.filter((p) => p.status === "review"),
    published: rangedPosts.filter((p) => p.status === "published"),
  }), [rangedPosts]);

  const statusCounts = {
    all: rangedPosts.length,
    draft: grouped.draft.length,
    review: grouped.review.length,
    published: grouped.published.length,
  };

  const filteredPosts = useMemo(() => {
    if (statusFilter === "all") return rangedPosts;
    return rangedPosts.filter((p) => p.status === statusFilter);
  }, [rangedPosts, statusFilter]);

  const anchorIsToday = isSameDay(anchor, new Date());

  return (
    <Layout
      title="Content Board"
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={runBackfill}
            disabled={backfilling}
            data-testid="backfill-published-btn"
            title="Import your already-published LinkedIn posts into the board (no duplicates)"
          >
            {backfilling
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Download className="w-3.5 h-3.5 mr-1.5" />}
            {backfilling ? "Importing…" : "Import published"}
          </Button>
          <Dialog
            open={dialogOpen}
            onOpenChange={(o) => {
              setDialogOpen(o);
              if (!o) setEditing(null);
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="btn-primary" data-testid="create-post-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New post
              </Button>
            </DialogTrigger>
            {dialogOpen && (
              <PostDialog
                key={editing?.id || "new"}
                workspaceId={workspaceId}
                editing={editing}
                voiceProfiles={voiceProfiles}
                onClose={() => setDialogOpen(false)}
                onSaved={() => { setDialogOpen(false); setEditing(null); load(); }}
              />
            )}
          </Dialog>
        </div>
      }
    >
      {/* Voice filter pill row */}
      {voiceProfiles.length > 1 && (
        <div className="flex items-center gap-1 mb-3" data-testid="posts-voice-filter">
          <button
            onClick={() => setVoiceFilter("")}
            data-testid="posts-voice-all"
            className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
              voiceFilter === ""
                ? "border-terracotta bg-terracotta/10 text-terracotta"
                : "border-hairline bg-graphite text-stone hover:text-parchment"
            }`}
          >
            All voices
          </button>
          {voiceProfiles.map((vp) => {
            const active = voiceFilter === vp.id;
            const c = voiceColor(vp.id);
            return (
              <button
                key={vp.id}
                onClick={() => setVoiceFilter(vp.id)}
                data-testid={`posts-voice-${vp.id}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
                  active
                    ? "border-terracotta bg-terracotta/10 text-terracotta"
                    : "border-hairline bg-graphite text-stone hover:text-parchment"
                }`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: c.swatch }} />
                {vp.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Toolbar row 1 — period picker (left) + view switcher (right) */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4" data-testid="posts-period-bar">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAnchor(shiftAnchor(period, anchor, -1))}
            className="p-2 rounded-md border border-hairline bg-graphite hover:bg-graphite-2 text-stone transition-colors"
            data-testid="posts-period-prev"
            aria-label="Previous period"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="px-4 py-2 rounded-md border border-hairline bg-graphite text-parchment inline-flex items-center gap-2 min-w-[220px] justify-center hover:border-terracotta transition-colors"
                data-testid="posts-period-label"
              >
                <CalendarIcon className="w-3.5 h-3.5 text-terracotta" />
                <span className="text-sm font-semibold">{labelRange(period, rangeStart, rangeEnd)}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              className="w-auto p-0 bg-graphite border-hairline rounded-xl shadow-xl"
              data-testid="posts-period-calendar"
            >
              <CalendarPicker
                mode="single"
                selected={anchor}
                onSelect={(d) => d && setAnchor(d)}
                initialFocus
                weekStartsOn={1}
              />
            </PopoverContent>
          </Popover>
          <button
            onClick={() => setAnchor(shiftAnchor(period, anchor, 1))}
            className="p-2 rounded-md border border-hairline bg-graphite hover:bg-graphite-2 text-stone transition-colors"
            data-testid="posts-period-next"
            aria-label="Next period"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {!anchorIsToday && (
            <button
              onClick={() => setAnchor(new Date())}
              className="text-xs text-stone hover:text-parchment inline-flex items-center gap-1 px-2 py-1 rounded transition-colors"
              data-testid="posts-period-today"
            >
              <RotateCcw className="w-3 h-3" /> Today
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex items-center bg-graphite border border-hairline rounded-md p-1" data-testid="posts-period-switch">
            {["day", "week", "month"].map((k) => {
              const active = period === k;
              return (
                <button
                  key={k}
                  onClick={() => setPeriod(k)}
                  data-testid={`posts-period-${k}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-sm capitalize transition-colors ${
                    active ? "bg-graphite-2 text-terracotta" : "text-stone hover:text-parchment"
                  }`}
                >
                  {k}
                </button>
              );
            })}
          </div>
          <div className="inline-flex items-center bg-graphite border border-hairline rounded-md p-1" data-testid="posts-view-toggle">
            {Object.entries(VIEW_META).map(([k, m]) => {
              const active = view === k;
              return (
                <button
                  key={k}
                  onClick={() => setView(k)}
                  data-testid={`view-${k}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5 transition-colors ${
                    active ? "bg-graphite-2 text-terracotta" : "text-stone hover:text-parchment"
                  }`}
                >
                  <m.Icon className="w-3.5 h-3.5" strokeWidth={2} />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Toolbar row 2 — prominent status strip (clickable filters in non-kanban views) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5" data-testid="posts-status-strip">
        {STATUS_STRIP.map((s) => {
          const active = view !== "kanban" && statusFilter === s.k;
          const clickable = view !== "kanban";
          return (
            <button
              key={s.k}
              disabled={!clickable}
              onClick={() => clickable && setStatusFilter(s.k)}
              data-testid={`posts-status-${s.k}`}
              className={`text-left px-4 py-3 rounded-lg border border-hairline ${s.bg} transition-all ${
                clickable ? "hover:-translate-y-0.5 hover:border-hairline-hover cursor-pointer" : "cursor-default"
              } ${active ? `ring-2 ${s.ring}` : ""}`}
            >
              <div className={`overline text-[10px] font-semibold ${s.fg} opacity-90`}>{s.label}</div>
              <div className={`text-3xl font-bold tabular-nums mt-0.5 ${s.fg}`}>{statusCounts[s.k]}</div>
            </button>
          );
        })}
      </div>

      {counts.overdue > 0 && (
        <div className="text-xs text-rose-400 inline-flex items-center gap-1 mb-3" data-testid="posts-overdue">
          <AlertTriangle className="w-3 h-3" /> {counts.overdue} overdue overall
        </div>
      )}

      {loading ? (
        <div className="overline">loading…</div>
      ) : rangedPosts.length === 0 ? (
        posts.length === 0 ? (
          <EmptyState onAdd={openCreate} />
        ) : (
          <div className="surface p-12 text-center max-w-xl mx-auto" data-testid="posts-empty-range">
            <FileText className="w-8 h-8 text-stone mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-parchment mb-1">Nothing in this range.</h3>
            <p className="text-sm text-stone">Try a wider window or jump back to today.</p>
          </div>
        )
      ) : view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kanban-board">
          {STATUSES.map((col) => (
            <KanbanColumn
              key={col.k}
              col={col}
              posts={grouped[col.k] || []}
              onEdit={openEdit}
              onDelete={removePost}
              onMove={moveStatus}
            />
          ))}
        </div>
      ) : view === "list" ? (
        <ListView posts={filteredPosts} onEdit={openEdit} onDelete={removePost} onMove={moveStatus} />
      ) : view === "gallery" ? (
        <PostsGalleryView posts={filteredPosts} onEdit={openEdit} onDelete={removePost} />
      ) : (
        <PostsTimelineView posts={filteredPosts} onEdit={openEdit} />
      )}
    </Layout>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="surface p-12 text-center max-w-xl mx-auto" data-testid="posts-empty">
      <div className="overline mb-3">empty pipeline</div>
      <h2 className="heading text-2xl text-zinc-100 mb-2">No posts in flight.</h2>
      <p className="text-sm text-zinc-400 mb-6 max-w-md mx-auto">
        This is where every post lives from idea to published. Add a draft, attach a visual,
        set a due date, move it through review, and it'll automatically count toward your client's contract.
      </p>
      <Button onClick={onAdd} className="btn-primary" data-testid="empty-create-post-btn">
        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add first post
      </Button>
    </div>
  );
}

function KanbanColumn({ col, posts, onEdit, onDelete, onMove }) {
  const dotColor = {
    zinc: "bg-zinc-500",
    amber: "bg-amber-400",
    emerald: "bg-emerald-400",
  }[col.color];

  return (
    <div
      data-testid={`kanban-col-${col.k}`}
      className="bg-zinc-900/40 border border-zinc-800 rounded-md p-3 min-h-[60vh]"
    >
      <header className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${dotColor}`} />
          <span className="text-sm text-zinc-200 capitalize">{col.label}</span>
          <span className="overline text-zinc-500" style={{ fontSize: 9 }}>{posts.length}</span>
        </div>
      </header>
      <div className="space-y-2">
        {posts.length === 0 && (
          <div className="text-xs text-zinc-600 px-2 py-3">No posts here yet.</div>
        )}
        {posts.map((p) => (
          <PostCard
            key={p.id}
            post={p}
            currentStatus={col.k}
            onEdit={() => onEdit(p)}
            onDelete={() => onDelete(p.id)}
            onMove={(next) => onMove(p, next)}
          />
        ))}
      </div>
    </div>
  );
}

function PostCard({ post, currentStatus, onEdit, onDelete, onMove }) {
  const due = post.due_date;
  const overdue = due && currentStatus !== "published" && due < new Date().toISOString().slice(0, 10);
  const hasAsset = post.image_url || post.pdf_url || post.video_url || post.attachment_link;

  const nextStatus =
    currentStatus === "draft" ? "review" :
    currentStatus === "review" ? "published" :
    null;
  const prevStatus =
    currentStatus === "published" ? "review" :
    currentStatus === "review" ? "draft" :
    null;

  const vc = voiceColor(post.voice_profile_id);
  return (
    <article
      data-testid={`post-card-${post.id}`}
      className="bg-zinc-950/70 border border-zinc-800 rounded-md p-3 cursor-pointer hover:border-zinc-700 transition-colors relative"
      style={post.voice_profile_id ? { borderLeft: `3px solid ${vc.swatch}` } : undefined}
      onClick={onEdit}
    >
      {(post.post_caption || "").length > 0 && (
        <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
          <CopyIcon
            text={post.post_caption || ""}
            testid={`post-card-copy-${post.id}`}
            label=""
            className="bg-zinc-900/80 backdrop-blur px-1.5 py-0.5 border border-zinc-800 hover:border-zinc-600"
          />
        </div>
      )}
      <div className="flex items-start gap-2 mb-2">
        <div className="min-w-0 flex-1 pr-8">
          <h3 className="text-sm text-zinc-100 truncate" data-testid={`post-title-${post.id}`}>
            {post.title || <span className="text-zinc-500 italic">untitled</span>}
          </h3>
          {post.voice_profile_name && (
            <div className="mt-0.5">
              <VoiceChip
                voiceId={post.voice_profile_id}
                voiceName={post.voice_profile_name}
                variant="dark"
                size="sm"
                testid={`post-voice-chip-${post.id}`}
              />
            </div>
          )}
        </div>
      </div>

      {post.post_caption && (
        <p className="text-xs text-zinc-400 line-clamp-2 mb-2 leading-snug whitespace-pre-wrap">
          {post.post_caption.slice(0, 140)}
          {post.post_caption.length > 140 ? "…" : ""}
        </p>
      )}

      {hasAsset && (
        <div className="flex items-center gap-1.5 mb-2 text-zinc-400">
          {post.image_url && <ImageIcon className="w-3.5 h-3.5" />}
          {post.pdf_url && <FileText className="w-3.5 h-3.5" />}
          {post.video_url && <Film className="w-3.5 h-3.5" />}
          {post.attachment_link && <Link2 className="w-3.5 h-3.5" />}
        </div>
      )}

      <ClientFeedbackBadge post={post} />

      <div className="flex items-center justify-between gap-2 text-xs">
        {due ? (
          <span
            className={`inline-flex items-center gap-1 ${
              overdue ? "text-rose-400" : "text-zinc-500"
            }`}
            data-testid={`post-due-${post.id}`}
          >
            <CalendarIcon className="w-3 h-3" />
            {due}
            {overdue && <span className="text-[10px] uppercase tracking-wider">overdue</span>}
          </span>
        ) : (
          <span className="text-zinc-600 text-xs">no due date</span>
        )}
        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {prevStatus && (
            <button
              onClick={() => onMove(prevStatus)}
              data-testid={`back-${post.id}`}
              className="text-zinc-500 hover:text-zinc-200 px-1.5 py-0.5"
              title={`Move to ${prevStatus}`}
            >
              ←
            </button>
          )}
          {nextStatus && (
            <button
              onClick={() => onMove(nextStatus)}
              data-testid={`next-${post.id}`}
              className="text-zinc-300 hover:text-emerald-300 px-1.5 py-0.5"
              title={`Move to ${nextStatus}`}
            >
              →
            </button>
          )}
          <button
            onClick={onDelete}
            data-testid={`delete-${post.id}`}
            className="text-zinc-500 hover:text-rose-400 px-1.5 py-0.5"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Gallery + Timeline views (dark theme, internal side)
// ---------------------------------------------------------------------------
function PostsGalleryView({ posts, onEdit, onDelete }) {
  if (posts.length === 0) {
    return <div className="surface p-8 text-center text-sm text-stone" data-testid="gallery-empty">No posts to show.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="posts-gallery">
      {posts.map((p) => {
        const vc = voiceColor(p.voice_profile_id);
        return (
          <button
            key={p.id}
            onClick={() => onEdit(p)}
            data-testid={`gallery-card-${p.id}`}
            className="surface p-4 text-left hover:border-hairline-hover transition-all hover:-translate-y-0.5 flex flex-col"
            style={{ borderLeft: `3px solid ${vc.swatch}` }}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <VoiceChip profileId={p.voice_profile_id} voiceName={p.voice_profile_name} />
              {p.due_date && (
                <span className="mono text-[10px] text-stone">{p.due_date.slice(0, 10)}</span>
              )}
              <StatusBadge status={p.status} />
            </div>
            {p.title && (
              <div className="text-sm text-parchment font-semibold leading-snug mb-1 line-clamp-2">{p.title}</div>
            )}
            <p className="text-[13px] text-stone leading-relaxed line-clamp-4 whitespace-pre-wrap flex-1">
              {p.post_caption || ""}
            </p>
            <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-hairline">
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(p); }}
                className="p-1 text-stone hover:text-parchment transition-colors"
                data-testid={`gallery-edit-${p.id}`}
                aria-label="Edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                className="p-1 text-stone hover:text-rose-400 transition-colors"
                data-testid={`gallery-delete-${p.id}`}
                aria-label="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PostsTimelineView({ posts, onEdit }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const p of posts) {
      const d = (p.due_date || p.published_at || p.created_at || "").slice(0, 7) || "unknown";
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(p);
    }
    return [...map.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, its]) => {
        let label = "Undated";
        if (key !== "unknown") {
          const [y, m] = key.split("-");
          label = new Date(+y, +m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
        }
        return { key, label, items: its };
      });
  }, [posts]);

  if (posts.length === 0) {
    return <div className="surface p-8 text-center text-sm text-stone">No posts to show.</div>;
  }
  return (
    <div className="space-y-6" data-testid="posts-timeline">
      {groups.map((g) => (
        <div key={g.key} data-testid={`posts-timeline-group-${g.key}`}>
          <div className="flex items-baseline gap-3 mb-3 pb-1 border-b border-hairline">
            <h4 className="text-lg font-semibold text-parchment">{g.label}</h4>
            <span className="mono text-[10px] uppercase tracking-wider text-stone">
              {g.items.length} post{g.items.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="relative pl-6">
            <div className="absolute left-[7px] top-1 bottom-1 w-px bg-hairline" />
            <div className="space-y-2">
              {g.items.map((p) => {
                const vc = voiceColor(p.voice_profile_id);
                return (
                  <button
                    key={p.id}
                    onClick={() => onEdit(p)}
                    data-testid={`timeline-row-${p.id}`}
                    className="w-full text-left relative"
                  >
                    <div
                      className="absolute -left-6 top-2 w-3 h-3 rounded-full ring-2 ring-obsidian"
                      style={{ background: vc.swatch }}
                    />
                    <div className="surface p-3 hover:border-hairline-hover transition-colors">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <VoiceChip profileId={p.voice_profile_id} voiceName={p.voice_profile_name} />
                        {p.due_date && (
                          <span className="mono text-[10px] text-stone">{p.due_date.slice(0, 10)}</span>
                        )}
                        <StatusBadge status={p.status} />
                      </div>
                      {p.title && (
                        <div className="text-sm text-parchment font-semibold leading-snug">{p.title}</div>
                      )}
                      <p className="text-[13px] text-stone leading-relaxed line-clamp-2 whitespace-pre-wrap">
                        {p.post_caption || ""}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


function ListView({ posts, onEdit, onDelete, onMove }) {
  return (
    <div className="surface overflow-hidden">
      <table className="w-full text-sm" data-testid="posts-list-table">
        <thead>
          <tr className="text-left text-zinc-500 border-b border-zinc-800">
            <th className="py-3 px-4 overline">title</th>
            <th className="py-3 px-2 overline">profile</th>
            <th className="py-3 px-2 overline">status</th>
            <th className="py-3 px-2 overline">due</th>
            <th className="py-3 px-2 overline">assets</th>
            <th className="py-3 px-2 overline">actions</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((p) => (
            <tr key={p.id} className="border-b border-zinc-900 hover:bg-zinc-900/30 cursor-pointer" onClick={() => onEdit(p)} data-testid={`post-row-${p.id}`}>
              <td className="py-3 px-4 max-w-md">
                <div className="text-zinc-100 truncate">{p.title || <em className="text-zinc-500">untitled</em>}</div>
                <div className="text-xs text-zinc-500 truncate">{(p.post_caption || "").slice(0, 100)}</div>
              </td>
              <td className="py-3 px-2 text-zinc-400">
                <VoiceChip
                  voiceId={p.voice_profile_id}
                  voiceName={p.voice_profile_name}
                  variant="dark"
                  size="sm"
                  testid={`post-row-voice-${p.id}`}
                /> {!p.voice_profile_name && "—"}
              </td>
              <td className="py-3 px-2">
                <StatusBadge status={p.status} />
              </td>
              <td className="py-3 px-2 text-zinc-400">{p.due_date || "—"}</td>
              <td className="py-3 px-2 text-zinc-400">
                <div className="flex items-center gap-1.5">
                  {p.image_url && <ImageIcon className="w-3.5 h-3.5" />}
                  {p.pdf_url && <FileText className="w-3.5 h-3.5" />}
                  {p.video_url && <Film className="w-3.5 h-3.5" />}
                  {p.attachment_link && <Link2 className="w-3.5 h-3.5" />}
                </div>
              </td>
              <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2">
                  <button onClick={() => onEdit(p)} className="text-zinc-400 hover:text-zinc-200" data-testid={`row-edit-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(p.id)} className="text-zinc-500 hover:text-rose-400" data-testid={`row-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: { color: "bg-zinc-800 text-zinc-300", label: "draft" },
    review: { color: "bg-amber-900/40 text-amber-300", label: "review" },
    published: { color: "bg-emerald-900/40 text-emerald-300", label: "published" },
  };
  const m = map[status] || map.draft;
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${m.color}`}>
      {m.label}
    </span>
  );
}

// ============================================================
// POST DIALOG — create / edit
// ============================================================
function PostDialog({ workspaceId, editing, voiceProfiles, onClose, onSaved }) {
  const [title, setTitle] = useState(editing?.title || "");
  const [caption, setCaption] = useState(editing?.post_caption || "");
  const [voiceId, setVoiceId] = useState(editing?.voice_profile_id || "");
  const [dueDate, setDueDate] = useState(editing?.due_date || "");
  const [link, setLink] = useState(editing?.attachment_link || "");
  const [status, setStatus] = useState(editing?.status || "draft");
  const [imageUrl, setImageUrl] = useState(editing?.image_url || "");
  const [pdfUrl, setPdfUrl] = useState(editing?.pdf_url || "");
  const [videoUrl, setVideoUrl] = useState(editing?.video_url || "");
  const [postId, setPostId] = useState(editing?.id || null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null); // image | pdf | video | null

  const saveBase = async () => {
    const body = {
      title: title.trim(),
      post_caption: caption,
      voice_profile_id: voiceId || null,
      due_date: dueDate || null,
      attachment_link: link.trim(),
      status,
      image_url: imageUrl,
      pdf_url: pdfUrl,
      video_url: videoUrl,
    };
    if (postId) {
      const r = await apiPatch(`/workspaces/${workspaceId}/posts/${postId}`, body);
      return r;
    }
    const r = await apiPost(`/workspaces/${workspaceId}/posts`, body);
    setPostId(r.id);
    return r;
  };

  const submit = async () => {
    setBusy(true);
    try {
      await saveBase();
      toast.success(postId ? "Post saved" : "Post created");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    // Ensure post exists first
    let pid = postId;
    if (!pid) {
      try {
        const saved = await saveBase();
        pid = saved.id;
      } catch (e) {
        toast.error("Save the post first");
        return;
      }
    }
    setUploading(kind);
    try {
      const form = new FormData();
      form.append("kind", kind);
      form.append("file", file);
      const r = await api.post(`/workspaces/${workspaceId}/posts/${pid}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (kind === "image") setImageUrl(url);
      if (kind === "pdf") setPdfUrl(url);
      if (kind === "video") setVideoUrl(url);
      toast.success(`${kind} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Upload failed`);
    } finally {
      setUploading(null);
    }
  };

  return (
    <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-zinc-100">{editing ? "Edit post" : "New post"}</DialogTitle>
        <DialogDescription className="text-xs text-zinc-500">
          Draft → Review → Published. Upload an image, PDF, or video; or paste an external Canva/Figma link.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        <div>
          <Label className="text-xs text-zinc-400">title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="working title (e.g. 'Why most engineers waste 4 hours/day')"
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="post-title-input"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-zinc-400">post caption (full text)</Label>
            {(caption || "").length > 0 && (
              <CopyIcon
                text={caption}
                testid="post-caption-copy"
                label="copy whole caption"
                className="text-zinc-500 hover:text-zinc-200"
              />
            )}
          </div>
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={8}
            placeholder="paste or draft the post here. once published, this text is matched against scraped engagement automatically."
            className="bg-zinc-900 border-zinc-800 text-zinc-100 whitespace-pre-wrap"
            data-testid="post-caption-input"
            style={{ whiteSpace: "pre-wrap" }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">voice profile</Label>
            <select
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 py-2 rounded-md text-sm"
              data-testid="post-voice-select"
            >
              <option value="">unassigned</option>
              {voiceProfiles.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">due date</Label>
            <Input
              type="date"
              value={dueDate || ""}
              onChange={(e) => setDueDate(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="post-due-input"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-zinc-400">status</Label>
          <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-md p-1 w-fit" data-testid="post-status-toggle">
            {STATUSES.map((s) => (
              <button
                key={s.k}
                onClick={() => setStatus(s.k)}
                data-testid={`status-${s.k}`}
                className={`text-xs px-3 py-1.5 rounded-sm capitalize transition-colors ${
                  status === s.k ? "bg-zinc-800 text-zinc-50" : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Assets */}
        <div className="space-y-3 pt-2 border-t border-zinc-800">
          <div className="overline">visual + assets</div>
          <AssetRow
            kind="image"
            label="single image (LinkedIn)"
            icon={ImageIcon}
            url={imageUrl}
            uploading={uploading === "image"}
            accept="image/*"
            onUpload={(f) => uploadAsset("image", f)}
            onClear={() => setImageUrl("")}
          />
          <AssetRow
            kind="pdf"
            label="PDF (for carousels)"
            icon={FileText}
            url={pdfUrl}
            uploading={uploading === "pdf"}
            accept=".pdf"
            onUpload={(f) => uploadAsset("pdf", f)}
            onClear={() => setPdfUrl("")}
          />
          <AssetRow
            kind="video"
            label="video"
            icon={Film}
            url={videoUrl}
            uploading={uploading === "video"}
            accept="video/*"
            onUpload={(f) => uploadAsset("video", f)}
            onClear={() => setVideoUrl("")}
          />
          <div>
            <Label className="text-xs text-zinc-400">external link (Canva / Figma / etc.)</Label>
            <Input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://canva.com/design/…"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="post-link-input"
            />
          </div>
        </div>

        {editing && editing.published_my_post_id && (
          <div className="bg-emerald-950/30 border border-emerald-900/40 rounded-md p-3 text-xs text-emerald-300" data-testid="post-linked-banner">
            ✓ Linked to live LinkedIn post · engagement metrics will appear in Reports.
          </div>
        )}

        {editing && (
          <ClientFeedbackPanel
            workspaceId={workspaceId}
            post={editing}
            onApplyDraft={(text) => setCaption(text)}
            onResolved={onSaved}
          />
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
          <button onClick={onClose} className="btn-ghost" data-testid="post-cancel-btn">Cancel</button>
          <Button onClick={submit} disabled={busy} className="btn-primary" data-testid="post-submit-btn">
            {busy ? "Saving…" : (editing ? "Save" : "Create")}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

function AssetRow({ kind, label, icon: Icon, url, uploading, accept, onUpload, onClear }) {
  const fileRef = React.useRef(null);
  const filename = url ? url.split("/").pop() : "";
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-md p-3" data-testid={`asset-row-${kind}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-zinc-300">
          <Icon className="w-4 h-4 text-zinc-500" /> {label}
        </div>
        <div className="flex items-center gap-2">
          {url ? (
            <>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-zinc-400 hover:text-sky-300 inline-flex items-center gap-1"
                data-testid={`asset-view-${kind}`}
              >
                <Eye className="w-3.5 h-3.5" /> {filename}
              </a>
              <button
                onClick={onClear}
                className="text-zinc-500 hover:text-rose-400"
                data-testid={`asset-clear-${kind}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-ghost text-xs"
              data-testid={`asset-upload-${kind}`}
            >
              <Upload className="w-3.5 h-3.5 mr-1 inline" /> {uploading ? "uploading…" : "upload"}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept={accept}
            className="hidden"
            data-testid={`asset-file-${kind}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
          />
        </div>
      </div>
    </div>
  );
}


// ============================================================
// CLIENT FEEDBACK — surfaced inside the post editor
// ============================================================
function ClientFeedbackBadge({ post }) {
  const items = post.client_feedback || [];
  const open = items.filter((i) => !i.resolved);
  const approved = !!post.client_approved;
  if (!approved && open.length === 0) return null;
  return (
    <div className="flex items-center gap-2 mb-2 text-[11px]">
      {approved && (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold" style={{ color: "#0E7A56", background: "rgba(14,159,110,0.1)", border: "1px solid rgba(14,159,110,0.35)" }}>
          <CheckCircle2 className="w-3 h-3" /> Approved
        </span>
      )}
      {open.length > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold" style={{ color: "#92400E", background: "rgba(180,83,9,0.1)", border: "1px solid rgba(180,83,9,0.35)" }}
          data-testid={`fb-badge-${post.id}`}
        >
          <MessageSquare className="w-3 h-3" /> {open.length} new from client
        </span>
      )}
    </div>
  );
}

function ClientFeedbackPanel({ workspaceId, post, onApplyDraft, onResolved }) {
  const items = post.client_feedback || [];
  const sorted = [...items].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  if (sorted.length === 0 && !post.client_approved) return null;

  const resolve = async (fbId) => {
    try {
      await apiPatch(`/workspaces/${workspaceId}/posts/${post.id}/feedback/${fbId}/resolve`, {});
      toast.success("marked resolved");
      onResolved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not resolve");
    }
  };

  return (
    <div className="pt-3 border-t border-zinc-800" data-testid="client-feedback-panel">
      <div className="overline mb-2 flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5" /> client feedback · {sorted.length}
      </div>
      {post.client_approved && (
        <div className="mb-3 bg-emerald-950/30 border border-emerald-900/40 px-3 py-2 text-xs text-emerald-300 inline-flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" />
          approved by {post.client_approved_by} · {(post.client_approved_at || "").slice(0, 10)}
        </div>
      )}
      <div className="space-y-2">
        {sorted.map((f) => (
          <div
            key={f.id}
            className={`border ${f.resolved ? "border-zinc-900 opacity-60" : "border-zinc-800"} bg-zinc-950/60 p-3 text-sm`}
            data-testid={`fb-item-${f.id}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-zinc-100 font-medium">{f.author_name}</span>
                <span className="mono text-[10px] uppercase tracking-wider text-zinc-500">
                  {f.kind === "approval" ? "approved"
                    : f.kind === "draft_submission" ? "submitted a draft"
                    : "recommended a change"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="mono text-[10px] text-zinc-500">{(f.created_at || "").slice(0, 10)}</span>
                {!f.resolved && f.kind !== "approval" && (
                  <button
                    onClick={() => resolve(f.id)}
                    className="text-[10px] text-zinc-500 hover:text-emerald-300 mono uppercase tracking-wider"
                    data-testid={`fb-resolve-${f.id}`}
                  >
                    mark done
                  </button>
                )}
              </div>
            </div>
            {f.content && (
              <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">{f.content}</p>
            )}
            {f.kind === "draft_submission" && f.caption && (
              <div className="mt-2 bg-zinc-900/60 border border-zinc-800 p-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="mono text-[10px] uppercase tracking-wider text-zinc-500">
                    their version
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { navigator.clipboard.writeText(f.caption); toast.success("copied"); }}
                      className="text-[10px] text-zinc-500 hover:text-zinc-200 mono uppercase tracking-wider inline-flex items-center gap-1"
                      data-testid={`fb-copy-${f.id}`}
                    >
                      <Copy className="w-3 h-3" /> copy
                    </button>
                    <button
                      onClick={() => { onApplyDraft(f.caption); toast.success("loaded into caption"); }}
                      className="text-[10px] text-amber-300 hover:text-amber-200 mono uppercase tracking-wider inline-flex items-center gap-1"
                      data-testid={`fb-apply-${f.id}`}
                    >
                      <Sparkles className="w-3 h-3" /> use as caption
                    </button>
                  </div>
                </div>
                <pre className="whitespace-pre-wrap break-words font-sans text-xs text-zinc-200 leading-relaxed">
{f.caption}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
